/**
 * affiliate-referrals-page.tsx — Platform → Tiếp thị liên kết → Khách được giới thiệu & lead.
 * Route `/platform/affiliate/referrals` (?tab=leads). Gắn / đổi người giới thiệu cho tổ chức (lý do + nhật ký);
 * lead từ trang /r/:mã → "Tạo tổ chức" (hộp tạo tổ chức điền sẵn mã + lead).
 */
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Building2, Link2 } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/misc'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { formatVnd } from '@/lib/format-money'
import { platformApiError } from '@/lib/platform-client'
import { referralSourceLabel } from '@/lib/affiliate'
import { usePlatformOrgs } from '@/hooks/use-platform'
import {
  useAdminLeads, useAdminReferrals, useBindReferral, useUpdateLead, type AdminLead, type AdminReferral,
} from '@/hooks/use-platform-affiliate'
import { CreateOrgDialog, type CreateOrgInitial } from '../orgs-page'
import { FilterChips, fmtDate, fmtDateTime, ReasonDialog } from './affiliate-shared'

const LEAD_STATUS: Record<AdminLead['status'], { label: string; tone: 'warning' | 'default' | 'success' | 'secondary' }> = {
  new: { label: 'Mới', tone: 'warning' },
  contacted: { label: 'Đã liên hệ', tone: 'default' },
  converted: { label: 'Đã tạo tổ chức', tone: 'success' },
  rejected: { label: 'Bỏ qua', tone: 'secondary' },
}

export function AffiliateReferralsPage() {
  const [sp, setSp] = useSearchParams()
  const tab = sp.get('tab') === 'leads' ? 'leads' : 'referrals'
  return (
    <div className="space-y-5">
      <PageHeader title="Khách được giới thiệu & lead" description="Mỗi tổ chức gắn tối đa một đối tác; người giới thiệu được chốt sau đơn thanh toán đầu tiên." />
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v }, { replace: true })}>
        <TabsList>
          <TabsTrigger value="referrals">Khách được giới thiệu</TabsTrigger>
          <TabsTrigger value="leads">Lead từ link</TabsTrigger>
        </TabsList>
        <TabsContent value="referrals" className="mt-4"><ReferralsTab /></TabsContent>
        <TabsContent value="leads" className="mt-4"><LeadsTab /></TabsContent>
      </Tabs>
    </div>
  )
}

function ReferralsTab() {
  const [page, setPage] = useState(1)
  const [bindOpen, setBindOpen] = useState(false)
  const { data, isLoading } = useAdminReferrals(page)
  const columns: Column<AdminReferral>[] = [
    { key: 'org', header: 'Tổ chức', cell: (r) => <Link className="hover:text-primary" to={`/platform/companies/${r.orgId}`}>{r.orgName ?? r.orgId}</Link> },
    { key: 'partner', header: 'Đối tác', cell: (r) => <span>{r.partnerName} <span className="font-mono text-xs text-muted-foreground">({r.partnerCode})</span></span> },
    { key: 'source', header: 'Nguồn', cell: (r) => referralSourceLabel(r.source) },
    { key: 'at', header: 'Ghi nhận', cell: (r) => fmtDate(r.attributedAt) },
    { key: 'lock', header: 'Chốt', cell: (r) => r.lockedAt ? <Badge variant="success">Đã trả tiền {fmtDate(r.lockedAt)}</Badge> : <Badge variant="secondary">Chưa</Badge> },
    { key: 'until', header: 'Hưởng đến', cell: (r) => r.commissionUntil ? fmtDate(r.commissionUntil) : 'Không giới hạn' },
    { key: 'rev', header: 'Doanh thu tính HH', align: 'right', cell: (r) => formatVnd(r.revenue) },
    { key: 'hh', header: 'Hoa hồng', align: 'right', cell: (r) => formatVnd(r.commission) },
  ]
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setBindOpen(true)}><Link2 className="h-4 w-4" /> Gắn người giới thiệu cho tổ chức</Button>
      </div>
      <DataTable columns={columns} rows={data?.items ?? []} loading={isLoading} rowKey={(r) => r.id} emptyTitle="Chưa có khách được giới thiệu" />
      {data && data.total > data.limit && <Pagination page={page} limit={data.limit} total={data.total} onPageChange={setPage} />}
      <BindDialog open={bindOpen} onClose={() => setBindOpen(false)} />
    </div>
  )
}

function BindDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const bind = useBindReferral()
  const [q, setQ] = useState('')
  const [orgId, setOrgId] = useState('')
  const [code, setCode] = useState('')
  const [force, setForce] = useState(false)
  const orgs = usePlatformOrgs({ search: q.trim() || undefined, page: 1, pageSize: 8 })
  const list = useMemo(() => orgs.data?.items ?? [], [orgs.data])
  return (
    <ReasonDialog
      open={open}
      title="Gắn / đổi người giới thiệu"
      description="Tổ chức đã thanh toán thì người giới thiệu đã chốt — chỉ đổi được khi chọn 'Buộc đổi'. Không gắn được tổ chức của chính đối tác."
      pending={bind.isPending}
      onClose={onClose}
      onConfirm={(reason) => {
        if (!orgId || !code.trim()) { toast.error('Chọn tổ chức và nhập mã'); return }
        bind.mutate({ orgId, code: code.trim().toUpperCase(), reason, force }, {
          onSuccess: (r: { action: string }) => { toast.success(r.action === 'noop' ? 'Tổ chức đã gắn đối tác này' : 'Đã gắn người giới thiệu'); onClose() },
          onError: (e) => toast.error(platformApiError(e)),
        })
      }}
    >
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label>Tổ chức</Label>
          <Input placeholder="Tìm tên tổ chức…" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="max-h-40 overflow-y-auto rounded border">
            {list.map((o) => (
              <button key={o.id} type="button" onClick={() => setOrgId(o.id)}
                className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-muted ${orgId === o.id ? 'bg-primary/10 text-primary' : ''}`}>
                {o.name}
              </button>
            ))}
            {!list.length && <p className="p-3 text-sm text-muted-foreground">Không có kết quả</p>}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="bind-code">Mã đối tác</Label>
          <Input id="bind-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={20} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={force} onCheckedChange={(v) => setForce(v === true)} /> Buộc đổi dù tổ chức đã thanh toán
        </label>
      </div>
    </ReasonDialog>
  )
}

function LeadsTab() {
  const [status, setStatus] = useState('new')
  const [page, setPage] = useState(1)
  const [creating, setCreating] = useState<CreateOrgInitial | null>(null)
  const { data, isLoading } = useAdminLeads(status, page)
  const update = useUpdateLead()
  const setLead = (id: string, s: string) => update.mutate({ id, status: s }, { onError: (e) => toast.error(platformApiError(e)) })
  const columns: Column<AdminLead>[] = [
    {
      key: 'who', header: 'Khách',
      cell: (l) => (
        <div>
          <p className="font-medium">{l.fullName}{l.companyName ? ` — ${l.companyName}` : ''}</p>
          <p className="text-xs text-muted-foreground">{l.phone}{l.email ? ` · ${l.email}` : ''}</p>
          {l.note && <p className="max-w-[360px] truncate text-xs text-muted-foreground" title={l.note}>{l.note}</p>}
        </div>
      ),
    },
    { key: 'partner', header: 'Đối tác', cell: (l) => <span>{l.partnerName} <span className="font-mono text-xs text-muted-foreground">({l.partnerCode})</span></span> },
    { key: 'at', header: 'Gửi lúc', cell: (l) => fmtDateTime(l.createdAt) },
    { key: 'st', header: 'Trạng thái', cell: (l) => <Badge variant={LEAD_STATUS[l.status].tone}>{LEAD_STATUS[l.status].label}</Badge> },
    {
      key: 'act', header: '', align: 'right',
      cell: (l) => l.status === 'converted'
        ? (l.orgId ? <Link className="text-xs text-primary hover:underline" to={`/platform/companies/${l.orgId}`}>Xem tổ chức</Link> : null)
        : (
          <div className="flex justify-end gap-1">
            <Button size="sm" onClick={() => setCreating({
              orgName: l.companyName || l.fullName, ownerFullName: l.fullName, ownerEmail: l.email ?? '', referralCode: l.partnerCode, leadId: l.id,
            })}>
              <Building2 className="h-3.5 w-3.5" /> Tạo tổ chức
            </Button>
            {l.status === 'new' && <Button size="sm" variant="outline" onClick={() => setLead(l.id, 'contacted')}>Đã liên hệ</Button>}
            {l.status !== 'rejected' && <Button size="sm" variant="ghost" onClick={() => setLead(l.id, 'rejected')}>Bỏ qua</Button>}
          </div>
        ),
    },
  ]
  return (
    <div className="space-y-3">
      <FilterChips value={status} onChange={(v) => { setStatus(v); setPage(1) }}
        options={[['new', 'Mới'], ['contacted', 'Đã liên hệ'], ['converted', 'Đã tạo tổ chức'], ['rejected', 'Bỏ qua'], ['', 'Tất cả']]} />
      <DataTable columns={columns} rows={data?.items ?? []} loading={isLoading} rowKey={(l) => l.id} emptyTitle="Không có lead" />
      {data && data.total > data.limit && <Pagination page={page} limit={data.limit} total={data.total} onPageChange={setPage} />}
      <CreateOrgDialog open={!!creating} onOpenChange={(v) => { if (!v) setCreating(null) }} initial={creating ?? undefined} />
    </div>
  )
}
